import type { AssetState } from '~/types/asset'
import type { ConfigurationValue, ConfigurationValues } from '~/types/asset-configuration'

export const CLO_GAIN_STATES = {
  gain: 'CLO_GAIN_VALUE',
  lightOnTime: 'LIGHT_ON_TIME'
} as const

const RATED_HOURS_PARAMETER = 'ratedLifeHours'

// CLO_GAIN_VALUE is in percent (90 = 90.0 %), LIGHT_ON_TIME in seconds
const SECONDS_PER_HOUR = 3600

export type CloGainOutput =
  | { status: 'notAdjusted' }
  | { status: 'waitingBurnHours' }
  | { status: 'frozen'; outputPercent: number }
  | {
      status: 'adjusted'
      outputPercent: number
      burnHours: number
      ratedHours: number | null
      adjustedAt: string | null
    }

const toNumber = (value: ConfigurationValue | string | undefined): number | null => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const hasValue = (value: ConfigurationValue): boolean => value !== null && value !== ''

// `profile` holds the setCloGainProfile values stored on the asset, empty when no profile is set
export const resolveCloGainOutput = (states: AssetState[], profile: ConfigurationValues = {}): CloGainOutput => {
  const stateByKey = new Map(states.map((state) => [state.stateKey, state]))
  const gainState = stateByKey.get(CLO_GAIN_STATES.gain)
  const gain = toNumber(gainState?.stateValue)
  const lightOnTime = toNumber(stateByKey.get(CLO_GAIN_STATES.lightOnTime)?.stateValue)
  const hasProfile = Object.values(profile).some(hasValue)

  if (gain !== null && !hasProfile) return { status: 'frozen', outputPercent: gain }
  if (lightOnTime === null) return { status: 'waitingBurnHours' }
  if (gain === null) return { status: 'notAdjusted' }

  return {
    status: 'adjusted',
    outputPercent: gain,
    burnHours: Math.round(lightOnTime / SECONDS_PER_HOUR),
    ratedHours: toNumber(profile[RATED_HOURS_PARAMETER]),
    adjustedAt: gainState?.dateReceived || gainState?.emissionDate || null
  }
}
